import React, { useState, useEffect, useCallback } from 'react';
import affiliateApi, { getErrorMessage, AFFILIATE_BASE } from '../affiliateApi';

const NAVY = '#1a2535';
const ACCENT = '#4a9edd';
const GREEN = '#10b981';
const SURFACE = '#f9fafb';
const BORDER = '#e5e7eb';
const SUB = '#6b7280';

const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'referral', label: 'Referral link' },
  { key: 'tax', label: 'Tax info' },
  { key: 'history', label: 'Payout history' },
];

function fmtMoney(n) {
  return '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Keeps this surface out of search entirely — belt-and-suspenders alongside
// the backend's X-Robots-Tag header and the absence of any nav link to it.
function useNoindex() {
  useEffect(() => {
    document.title = 'Jobhub Partner Portal';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);
}

function RequestLinkGate({ initialError }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError || '');

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true); setError('');
    try {
      await affiliateApi.post('/request-link', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: SURFACE, fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, width: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img src="/jobhub-logo-light.png" alt="JobHub" style={{ height: 100, width: 'auto', objectFit: 'contain', marginBottom: 12 }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>Jobhub Partner Portal</div>
        </div>
        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📬</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 6 }}>Check your email</div>
            <div style={{ fontSize: 13, color: SUB }}>If that address is on our partner list, a login link is on its way. It expires in 15 minutes.</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: SUB, marginBottom: 16, textAlign: 'center' }}>
              This portal is invite-only. Enter your partner email and we'll send you a fresh login link.
            </div>
            <form onSubmit={submit}>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 8, border: `1.5px solid ${BORDER}`, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
              />
              {error && <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{error}</div>}
              <button type="submit" disabled={loading}
                style={{ width: '100%', padding: 12, background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Sending...' : 'Send me a login link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: SUB, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent || NAVY }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: SUB, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function DashboardTab({ affiliate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    affiliateApi.get('/dashboard').then(r => setData(r.data)).catch(err => setError(getErrorMessage(err)));
  }, []);

  if (error) return <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>;
  if (!data) return <div style={{ color: SUB, fontSize: 13 }}>Loading...</div>;

  return (
    <div>
      {data.held && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#854d0e', fontWeight: 600 }}>
          ⏱ {data.heldMessage} Complete your onboarding on the Tax Info tab to release accrued payouts.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        <MetricCard label="Active Clients" value={data.activeClientCount} />
        <MetricCard
          label="This Month's Payout"
          value={fmtMoney(data.thisMonthPayoutTotal)}
          sub={data.held ? 'Held — see banner above' : undefined}
          accent={data.held ? '#92400e' : GREEN}
        />
        <MetricCard label="Next Payment Date" value={fmtDate(data.nextPaymentDate)} />
        <MetricCard
          label="Next Month Estimate"
          value={fmtMoney(data.nextMonthEstimate.amount)}
          sub="Estimate — not yet accrued"
          accent={ACCENT}
        />
      </div>
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BORDER}`, fontWeight: 700, fontSize: 14, color: NAVY }}>Recent Activity</div>
        {!data.recentActivity.length ? (
          <div style={{ padding: 24, textAlign: 'center', color: SUB, fontSize: 13 }}>No activity yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: SURFACE }}>
                  {['Client', 'Date', 'Status', 'Amount'].map(h => (
                    <th key={h} style={{ padding: '8px 16px', textAlign: 'left', color: SUB, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recentActivity.map((row, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${BORDER}` }}>
                    <td style={{ padding: '10px 16px', color: NAVY, fontWeight: 600 }}>{row.client}</td>
                    <td style={{ padding: '10px 16px', color: SUB }}>{fmtDate(row.period)}</td>
                    <td style={{ padding: '10px 16px', color: data.held ? '#92400e' : SUB }}>{row.status}</td>
                    <td style={{ padding: '10px 16px', color: NAVY, fontWeight: 700 }}>{fmtMoney(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ReferralTab() {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { affiliateApi.get('/referral-info').then(r => setInfo(r.data)).catch(() => {}); }, []);

  const copy = () => {
    if (!info) return;
    navigator.clipboard.writeText(info.referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!info) return <div style={{ color: SUB, fontSize: 13 }}>Loading...</div>;

  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 24, maxWidth: 560 }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 6 }}>Your referral link</div>
      <div style={{ fontSize: 13, color: SUB, marginBottom: 16 }}>Share this link — anyone who signs up through it is attributed to you for 30 days from their last click.</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={info.referralUrl} style={{ flex: 1, padding: '11px 14px', borderRadius: 8, border: `1.5px solid ${BORDER}`, fontSize: 13, color: NAVY, background: SURFACE }} />
        <button onClick={copy} style={{ padding: '11px 18px', background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
      <div style={{ marginTop: 16, fontSize: 12, color: SUB }}>Referral code: <strong style={{ color: NAVY }}>{info.referralCode}</strong></div>
    </div>
  );
}

function TaxInfoTab({ affiliate }) {
  const Row = ({ done, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{ width: 20, height: 20, borderRadius: '50%', background: done ? GREEN : BORDER, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
        {done ? '✓' : ''}
      </span>
      <span style={{ fontSize: 13, color: done ? NAVY : SUB, fontWeight: done ? 600 : 400 }}>{label}</span>
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 640 }}>
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 14 }}>Onboarding checklist</div>
        <Row done={affiliate?.w9_received} label="W-9 received" />
        <Row done={affiliate?.contract_signed} label="Signed Ambassador Commission Agreement received" />
        <div style={{ fontSize: 12, color: SUB, marginTop: 6 }}>Both are required before your first payout is released. Accrued amounts are never lost — they're just held until both are on file.</div>
      </div>

      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 8 }}>1. W-9</div>
        <div style={{ fontSize: 13, color: SUB, marginBottom: 12 }}>Download, fill out, and email the completed form to <a href="mailto:w9@jobhubco.com" style={{ color: ACCENT }}>w9@jobhubco.com</a>.</div>
        <a href={`${AFFILIATE_BASE}/documents/w9`} target="_blank" rel="noreferrer" style={{ display: 'inline-block', padding: '10px 20px', background: NAVY, color: '#fff', textDecoration: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>Download W-9 →</a>
      </div>

      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 8 }}>2. Ambassador Commission Agreement</div>
        <div style={{ fontSize: 13, color: SUB, marginBottom: 12 }}>Download, sign, and email a copy to <a href="mailto:contracts@jobhubco.com" style={{ color: ACCENT }}>contracts@jobhubco.com</a>.</div>
        <a href={`${AFFILIATE_BASE}/documents/contract`} target="_blank" rel="noreferrer" style={{ display: 'inline-block', padding: '10px 20px', background: NAVY, color: '#fff', textDecoration: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>Download Agreement →</a>
      </div>
    </div>
  );
}

function HistoryTab() {
  const [data, setData] = useState(null);

  useEffect(() => { affiliateApi.get('/payouts').then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div style={{ color: SUB, fontSize: 13 }}>Loading...</div>;

  const TYPE_LABEL = { lump: 'Lump sum', recurring: 'Recurring', anniversary_bonus: 'Anniversary bonus' };

  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
      {!data.payouts.length ? (
        <div style={{ padding: 24, textAlign: 'center', color: SUB, fontSize: 13 }}>No payouts recorded yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: SURFACE }}>
                {['Client', 'Date', 'Type', 'Status', 'Amount'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: SUB, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.payouts.map(p => (
                <tr key={p.id} style={{ borderTop: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '10px 16px', color: NAVY, fontWeight: 600 }}>{p.client}</td>
                  <td style={{ padding: '10px 16px', color: SUB }}>{fmtDate(p.period)}</td>
                  <td style={{ padding: '10px 16px', color: SUB }}>{TYPE_LABEL[p.type] || p.type}</td>
                  <td style={{ padding: '10px 16px', color: p.status === 'paid' ? GREEN : SUB, fontWeight: 600, textTransform: 'capitalize' }}>{p.status}</td>
                  <td style={{ padding: '10px 16px', color: NAVY, fontWeight: 700 }}>{fmtMoney(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AffiliatePortal() {
  useNoindex();
  const [status, setStatus] = useState('checking'); // checking | gate | authed
  const [gateError, setGateError] = useState('');
  const [affiliate, setAffiliate] = useState(null);
  const [tab, setTab] = useState('dashboard');

  const loadMe = useCallback(() => {
    return affiliateApi.get('/me').then(r => {
      setAffiliate(r.data);
      setStatus('authed');
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      affiliateApi.post('/exchange-token', { token })
        .then(() => {
          window.history.replaceState({}, '', '/affiliate/portal');
          return loadMe();
        })
        .catch(err => {
          setGateError(getErrorMessage(err, 'This link is invalid or has expired.'));
          setStatus('gate');
        });
    } else {
      loadMe().catch(() => setStatus('gate'));
    }
  }, [loadMe]);

  if (status === 'checking') {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: SUB, fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif' }}>Loading...</div>;
  }
  if (status === 'gate') {
    return <RequestLinkGate initialError={gateError} />;
  }

  const logout = () => {
    affiliateApi.post('/logout').finally(() => { setStatus('gate'); setAffiliate(null); });
  };

  return (
    <div style={{ minHeight: '100vh', background: SURFACE, fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif' }}>
      <div style={{ background: NAVY, padding: '0 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>Jobhub Partner Portal</div>
            <div style={{ color: '#7aa3c0', fontSize: 12 }}>{affiliate?.business_name || affiliate?.name}</div>
          </div>
          <button onClick={logout} style={{ background: 'none', border: '1px solid #34455c', borderRadius: 8, padding: '7px 14px', color: '#cbd5e1', cursor: 'pointer', fontSize: 12 }}>Sign out</button>
        </div>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', gap: 4, overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: '10px 16px', background: 'none', border: 'none', borderBottom: tab === t.key ? `2px solid ${ACCENT}` : '2px solid transparent',
                color: tab === t.key ? '#fff' : '#94a3b8', fontWeight: tab === t.key ? 700 : 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
        {tab === 'dashboard' && <DashboardTab affiliate={affiliate} />}
        {tab === 'referral' && <ReferralTab />}
        {tab === 'tax' && <TaxInfoTab affiliate={affiliate} />}
        {tab === 'history' && <HistoryTab />}
      </div>
    </div>
  );
}
